/**
 * Survey Preview Component
 * Preview survey as respondents will see it
 */

import React from 'react';
import { X, Send } from 'lucide-react';

const SurveyPreview = ({ survey, onClose, onSend }) => {
  const ratingScale = [
    { value: 1, label: 'Never' },
    { value: 2, label: 'Rarely' },
    { value: 3, label: 'Sometimes' },
    { value: 4, label: 'Often' },
    { value: 5, label: 'Always' }
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '0.75rem',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)'
        }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', margin: 0, marginBottom: '0.25rem' }}>
              Survey Preview
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
              This is how respondents will see the survey
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
            }}
          >
            <X size={18} color="#374151" />
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ 
          padding: '2rem', 
          flex: 1, 
          overflowY: 'auto',
          background: 'linear-gradient(135deg, #fafbfc 0%, #f4f6f8 100%)'
        }}>
          {/* Survey Header */}
          <div style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              display: 'inline-block',
              padding: '0.25rem 0.75rem',
              background: '#dbeafe',
              color: '#1e40af',
              borderRadius: '9999px',
              fontSize: '0.75rem',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '1rem'
            }}>
              {survey.surveyType}
            </div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
              Leadership Competency Assessment
            </h3>
            <p style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '1rem' }}>
              Please rate the following behaviors based on your observations
            </p>
            <div style={{ 
              padding: '1rem',
              background: '#f9fafb',
              borderRadius: '0.5rem',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.5rem' }}>
                <strong>Participant:</strong> John Smith
              </div>
              <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.5rem' }}>
                <strong>Program:</strong> {survey.programLevel}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                <strong>Response Window:</strong> {survey.responseWindow} days
              </div>
            </div>
          </div>

          {/* Rating Scale Legend */}
          <div style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '700', color: '#111827', marginBottom: '1rem' }}>
              Rating Scale
            </h4>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {ratingScale.map(rating => (
                <div
                  key={rating.value}
                  style={{
                    flex: '1 1 150px',
                    padding: '0.75rem',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: '700', 
                    color: '#6366f1',
                    marginBottom: '0.25rem'
                  }}>
                    {rating.value}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>
                    {rating.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Questions */}
          {survey.questions && survey.questions.length > 0 ? (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {survey.questions.map((question, index) => (
                <div
                  key={question.id}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.75rem',
                    padding: '1.5rem',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ 
                      display: 'inline-block',
                      padding: '0.25rem 0.5rem',
                      background: '#f3f4f6',
                      color: '#6b7280',
                      borderRadius: '0.25rem',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      marginBottom: '0.5rem'
                    }}>
                      Question {index + 1}
                    </div>
                    <h5 style={{ fontSize: '1rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      {question.text || 'Question text will appear here'}
                    </h5>
                    {question.competencyId && (
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        Competency: {question.competencyId}
                      </div>
                    )}
                  </div>

                  {/* Rating Options */}
                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    {ratingScale.map(rating => (
                      <label
                        key={rating.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.75rem 1rem',
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.5rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f3f4f6';
                          e.currentTarget.style.borderColor = '#d1d5db';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#f9fafb';
                          e.currentTarget.style.borderColor = '#e5e7eb';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{
                            width: '2rem',
                            height: '2rem',
                            borderRadius: '50%',
                            border: '2px solid #d1d5db',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.875rem',
                            fontWeight: '700',
                            color: '#6b7280'
                          }}>
                            {rating.value}
                          </div>
                          <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                            {rating.label}
                          </span>
                        </div>
                        <input
                          type="radio"
                          name={`question-${question.id}`}
                          value={rating.value}
                          style={{ cursor: 'pointer' }}
                          disabled
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              background: '#ffffff',
              border: '1px dashed #d1d5db',
              borderRadius: '0.75rem',
              padding: '3rem 2rem',
              textAlign: 'center'
            }}>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                No questions added to this survey yet
              </p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '1.5rem',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#f9fafb'
        }}>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            {survey.questions?.length || 0} questions • {survey.responseWindow} day response window
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#ffffff',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f9fafb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#ffffff';
              }}
            >
              Close
            </button>
            {onSend && (
              <button
                onClick={onSend}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.25)';
                }}
              >
                <Send size={16} />
                <span>Send Survey</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyPreview;
